import React, { memo, useState, useCallback, useRef, useEffect } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef } from 'react-zoom-pan-pinch'
import tw from 'twin.macro'
import { useDispatch } from 'react-redux'
import { FileViewerControls } from './FileViewerControls'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { DownloadStepDocument } from 'src/constants/actionTypes'
import { handleApiError } from 'src/utils/errorHandlers'
import {
  getMimeTypeFromExtension,
  downloadFileFromBuffer,
  downloadFileFromUrl,
} from 'src/utils/fileDownload'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

const publicUrl = process.env.PUBLIC_URL || ''
pdfjs.GlobalWorkerOptions.workerSrc = `${publicUrl}/pdf.worker.min.mjs`

interface FileViewerWithControlsProps {
  filePath: string
  isPdf: boolean
  fileName?: string
  partnerDriverId?: string
  stepId?: string
  documentId?: string
  fileExtension?: string
  onClose?: () => void
}

const FileViewerWithControlsComponent = ({
  filePath,
  isPdf,
  fileName,
  partnerDriverId,
  stepId,
  documentId,
  fileExtension: fileExtensionProp,
}: FileViewerWithControlsProps) => {
  const dispatch = useDispatch()
  const [rotationValue, setRotationValue] = useState(0)
  const [disableTransition, setDisableTransition] = useState(false)
  const [numPages, setNumPages] = useState(0)
  const [pageNumber, setPageNumber] = useState(1)
  const minScale = 0.1
  const [currentScale, setCurrentScale] = useState(1)
  const [wrapperDimensions, setWrapperDimensions] = useState({ width: 0, height: 0 })
  const [isDownloading, setIsDownloading] = useState(false)
  const [pageWidth, setPageWidth] = useState<number | undefined>(undefined)
  const [isTransformReady, setIsTransformReady] = useState(false)

  const transformRef = useRef<ReactZoomPanPinchRef>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  const resetTransform = useCallback(() => {
    setTimeout(() => {
      transformRef.current?.resetTransform()
      transformRef.current?.centerView(1, 0)
    }, 100)
  }, [])

  const normalizeRotation = useCallback((value: number) => {
    return ((value % 360) + 360) % 360
  }, [])

  const handleRotate = useCallback(
    (direction: 'left' | 'right') => {
      setRotationValue((prev) => {
        const normalized = normalizeRotation(prev)
        const newValue = direction === 'left' ? normalized - 90 : normalized + 90
        const finalValue = normalizeRotation(newValue)

        if (
          (prev === 0 && finalValue === 270) ||
          (normalizeRotation(prev) === 270 && finalValue === 0)
        ) {
          setDisableTransition(true)
          setTimeout(() => setDisableTransition(false), 200)
        }

        return finalValue
      })
    },
    [normalizeRotation],
  )

  const handleRotateLeft = useCallback(() => handleRotate('left'), [handleRotate])
  const handleRotateRight = useCallback(() => handleRotate('right'), [handleRotate])

  const handleZoomIn = useCallback(() => {
    transformRef.current?.zoomIn(0.1)
  }, [])

  const handleZoomOut = useCallback(() => {
    if (!transformRef.current) {
      return
    }

    const currentState = transformRef.current.state
    const scale = currentState?.scale ?? 1

    if (!isPdf && scale <= minScale + 0.01) {
      return
    }

    transformRef.current.zoomOut(0.1)
  }, [minScale, isPdf])

  const handlePreviousPage = useCallback(() => {
    setPageNumber((prev) => Math.max(1, prev - 1))
  }, [])

  const handleNextPage = useCallback(() => {
    setPageNumber((prev) => Math.min(numPages, prev + 1))
  }, [numPages])

  const handleDownload = useCallback(async () => {
    try {
      setIsDownloading(true)

      if (partnerDriverId && stepId && documentId) {
        const buffer: ArrayBuffer = await createAsyncAction(
          dispatch,
          DownloadStepDocument.request({
            partnerDriverId,
            stepId,
            documentId,
          }),
        )

        const fileExtension = fileExtensionProp || fileName?.split('.').pop() || 'pdf'
        const downloadFileName = fileName || `document.${fileExtension}`
        const mimeType = getMimeTypeFromExtension(fileExtension)

        downloadFileFromBuffer(buffer, downloadFileName, mimeType)
      } else if (filePath) {
        await downloadFileFromUrl(filePath, fileName)
      }
    } catch (error) {
      handleApiError(error)
    } finally {
      setIsDownloading(false)
    }
  }, [partnerDriverId, stepId, documentId, filePath, fileName, fileExtensionProp, dispatch])

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setNumPages(numPages)
      setPageNumber(1)
      resetTransform()
    },
    [resetTransform],
  )

  const calculatePageWidth = useCallback(
    (page: { width: number; height: number }, currentRotation: number) => {
      const isRotated90or270 = currentRotation === 90 || currentRotation === 270
      const effectiveWidth = isRotated90or270 ? page.height : page.width
      const effectiveHeight = isRotated90or270 ? page.width : page.height

      if (wrapperDimensions.width > 0 && wrapperDimensions.height > 0) {
        const availableWidth = wrapperDimensions.width - 40
        const availableHeight = wrapperDimensions.height - 40

        const widthScale = availableWidth / effectiveWidth
        const heightScale = availableHeight / effectiveHeight
        const scale = Math.min(widthScale, heightScale, 1)

        return effectiveWidth * scale
      } else if (wrapperDimensions.width > 0) {
        return Math.min(wrapperDimensions.width - 40, effectiveWidth)
      }

      return undefined
    },
    [wrapperDimensions],
  )

  const onPageLoadSuccess = useCallback(
    (page: { width: number; height: number }) => {
      const calculatedWidth = calculatePageWidth(page, 0)
      setPageWidth(calculatedWidth)
      resetTransform()
    },
    [calculatePageWidth, resetTransform],
  )

  useEffect(() => {
    if (!filePath) {
      setIsTransformReady(true)

      return
    }

    setRotationValue(0)
    setCurrentScale(1)
    setWrapperDimensions({ width: 0, height: 0 })
    setIsTransformReady(false)

    if (isPdf) {
      setPageNumber(1)
    }

    resetTransform()
  }, [filePath, isPdf, resetTransform])

  useEffect(() => {
    if (!containerRef.current || !wrapperRef.current) {
      return
    }

    let rafId: number | null = null
    let isUpdating = false

    const updateDimensions = () => {
      if (isUpdating || !containerRef.current || !wrapperRef.current) {
        return
      }

      const width = containerRef.current.clientWidth
      const height = containerRef.current.clientHeight

      if (width > 0 && height > 100) {
        const { width: currentWidth, height: currentHeight } = wrapperDimensions

        const needsUpdate =
          currentWidth === 0 ||
          currentHeight === 0 ||
          Math.abs(currentWidth - width) > 2 ||
          Math.abs(currentHeight - height) > 2

        if (needsUpdate) {
          isUpdating = true
          wrapperRef.current.style.width = `${width}px`
          wrapperRef.current.style.height = `${height}px`
          setWrapperDimensions({ width, height })
          requestAnimationFrame(() => {
            isUpdating = false
          })
        }
      }
    }

    const handleResize = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }
      rafId = requestAnimationFrame(() => {
        updateDimensions()
        rafId = null
      })
    }

    requestAnimationFrame(() => {
      updateDimensions()
    })

    const resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(containerRef.current)

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }
      resizeObserver.disconnect()
    }
  }, [])

  useEffect(() => {
    const hasValidDimensions = wrapperDimensions.width > 0 && wrapperDimensions.height > 0

    if (isPdf && hasValidDimensions) {
      setPageWidth(undefined)
      resetTransform()
    }
  }, [wrapperDimensions, isPdf, resetTransform])

  const wrapperStyle = {
    width: wrapperDimensions.width > 0 ? `${wrapperDimensions.width}px` : '100%',
    height: wrapperDimensions.height > 0 ? `${wrapperDimensions.height}px` : '100%',
    maxWidth: wrapperDimensions.width > 0 ? `${wrapperDimensions.width}px` : '100%',
    maxHeight: wrapperDimensions.height > 0 ? `${wrapperDimensions.height}px` : '100%',
  }

  const isZoomOutDisabled = currentScale <= 1.01 || (!isPdf && currentScale <= minScale + 0.01)

  return (
    <div css={tw`relative w-full flex-1 min-h-0 flex flex-col overflow-hidden`}>
      <div ref={containerRef} css={tw`flex-1 min-h-0 overflow-hidden w-full`}>
        <div
          ref={wrapperRef}
          css={tw`overflow-hidden`}
          style={{ position: 'relative', ...wrapperStyle }}
        >
          <TransformWrapper
            key={filePath}
            ref={transformRef}
            initialScale={1}
            minScale={minScale}
            maxScale={3}
            centerOnInit={true}
            centerZoomedOut={true}
            limitToBounds={true}
            wheel={{
              disabled: true,
            }}
            pinch={{
              disabled: false,
            }}
            doubleClick={{ disabled: true }}
            onInit={() => {}}
            onTransformed={(ref) => {
              if (ref.state) {
                setCurrentScale(ref.state.scale ?? 1)
              }
            }}
          >
            <TransformComponent
              wrapperStyle={{
                ...wrapperStyle,
                overflow: 'hidden',
                position: 'relative',
              }}
              contentStyle={{
                width: '100%',
                height: '100%',
                padding: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
              }}
            >
              {isPdf ? (
                <div
                  css={tw`inline-block origin-center max-w-full max-h-full`}
                  style={{
                    transform: `rotate(${normalizeRotation(rotationValue)}deg)`,
                    transition: disableTransition ? 'none' : 'transform 0.2s ease-out',
                  }}
                >
                  <Document
                    file={filePath}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={<div css={tw`text-center py-8`}>Loading ...</div>}
                    error={<div css={tw`text-center py-8 text-red-500`}>Failed to load PDF</div>}
                    className="react-pdf__Document"
                  >
                    <Page
                      pageNumber={pageNumber}
                      rotate={0}
                      width={pageWidth}
                      onLoadSuccess={onPageLoadSuccess}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                      css={tw`shadow-lg`}
                      className="react-pdf__Page"
                    />
                  </Document>
                </div>
              ) : (
                <div
                  css={tw`flex items-center justify-center origin-center w-full h-full max-w-full max-h-full overflow-hidden relative box-border`}
                  style={{
                    transform: `rotate(${normalizeRotation(rotationValue)}deg)`,
                    transition: disableTransition ? 'none' : 'transform 0.2s ease-out',
                  }}
                >
                  <img
                    key={filePath}
                    ref={imageRef}
                    src={filePath}
                    alt="Preview"
                    css={tw`block max-w-full max-h-full w-auto h-auto object-contain box-border`}
                    style={{
                      opacity: isTransformReady ? 1 : 0,
                      transition: 'opacity 0.2s ease-in',
                    }}
                    onLoad={() => {
                      setIsTransformReady(true)
                    }}
                    onError={() => {}}
                  />
                </div>
              )}
            </TransformComponent>
          </TransformWrapper>
        </div>
      </div>
      <FileViewerControls
        isPdf={isPdf}
        numPages={numPages}
        pageNumber={pageNumber}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        onZoomOut={handleZoomOut}
        onZoomIn={handleZoomIn}
        isZoomOutDisabled={isZoomOutDisabled}
        onRotateRight={handleRotateRight}
        onRotateLeft={handleRotateLeft}
        partnerDriverId={partnerDriverId}
        stepId={stepId}
        documentId={documentId}
        filePath={filePath}
        onDownload={handleDownload}
        isDownloading={isDownloading}
      />
    </div>
  )
}

export const FileViewerWithControls = memo(FileViewerWithControlsComponent)
